/**
 * Authenticated HTTP client for making requests to the backend server.
 * Automatically includes Firebase ID tokens in requests.
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { User } from 'firebase/auth';
import { auth } from '../utils/firebaseSetUp';
import {
  clearSelfHostedSession,
  getSelfHostedUser,
  getValidSelfHostedToken,
  isSelfHostedMode,
  refreshSelfHostedToken,
} from './selfHostedAuth';
import { SERVER_API_BASE } from '../config/apiBase';

// Base URL for the server API
const SERVER_API_V1 = `${SERVER_API_BASE}/api/v1`;

class AuthenticatedHttpClient {
  private axiosInstance: AxiosInstance;
  private currentUser: User | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: SERVER_API_V1,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Set up request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async config => {
        try {
          if (isSelfHostedMode()) {
            const token = await getValidSelfHostedToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } else {
            const user = auth.currentUser;

            if (user) {
              const token = await user.getIdToken();
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
        } catch (error) {
          console.error('Error getting auth token:', error);
          // Don't throw here, let the request proceed without token
          // The server will handle authentication errors
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Set up response interceptor to handle auth errors
    this.axiosInstance.interceptors.response.use(
      response => {
        console.log(
          '✅ Response received:',
          response.status,
          response.config.url
        );
        return response;
      },
      async error => {
        console.log(
          '❌ Response error:',
          error.response?.status,
          error.config?.url,
          error.response?.data
        );

        const originalRequest = error.config;

        // If we get a 401 and haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (isSelfHostedMode()) {
            const token = await refreshSelfHostedToken();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.axiosInstance(originalRequest);
            }
            this.handleAuthFailure();
            return Promise.reject(error);
          }

          console.log('🔄 Attempting token refresh...');
          try {
            const user = auth.currentUser;
            if (user) {
              const token = await user.getIdToken(true);
              console.log('🔄 Token refreshed, retrying request');
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            this.handleAuthFailure();
          }
        }

        return Promise.reject(error);
      }
    );

    if (isSelfHostedMode()) {
      const stored = getSelfHostedUser();
      this.currentUser = stored
        ? ({ uid: stored.uid, email: stored.email ?? null } as User)
        : null;
    } else {
      auth.onAuthStateChanged(user => {
        this.currentUser = user;
      });
    }
  }

  private handleAuthFailure() {
    if (isSelfHostedMode()) {
      clearSelfHostedSession();
    } else {
      localStorage.removeItem('authToken');
    }
    window.location.href = '/auth';
  }

  // Generic request methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.get(
      url,
      config
    );
    return response.data;
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.post(
      url,
      data,
      config
    );
    return response.data;
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.put(
      url,
      data,
      config
    );
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.delete(
      url,
      config
    );
    return response.data;
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.patch(
      url,
      data,
      config
    );
    return response.data;
  }

  // Debug method to test authentication
  async testAuth(): Promise<void> {
    console.log('🧪 Testing authentication...');
    const user = isSelfHostedMode()
      ? getSelfHostedUser()
      : auth.currentUser;
    console.log(
      'Current user:',
      user && 'email' in user ? user.email : 'No user'
    );

    if (user) {
      try {
        const token = isSelfHostedMode()
          ? await getValidSelfHostedToken()
          : await (user as User).getIdToken();
        if (!token) {
          console.log('❌ No auth token');
          return;
        }
        console.log('Token length:', token.length);
        console.log('Token preview:', token.substring(0, 20) + '...');

        // Test a simple API call
        const response = await this.axiosInstance.get('/clients');
        console.log('✅ Test API call successful:', response.status);
      } catch (error) {
        console.error('❌ Test API call failed:', error);
      }
    } else {
      console.log('❌ No authenticated user');
    }
  }

  // Utility methods
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // Get the raw axios instance for advanced usage
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Create and export a singleton instance
export const authenticatedHttpClient = new AuthenticatedHttpClient();

// Export the class for testing or custom instances
export default AuthenticatedHttpClient;
